import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import CustomSelect from 'components/CustomSelect';
import Pagination from 'components/Pagination';
import FilterSearch from 'components/FilterSearch';
import FilterDropdown from 'components/FilterDropdown';
import RecipeModal from 'pages/production/modals/RecipeModal';

import { GET, httpOk } from 'helpers/api';
import { DOWNLOAD_FILE, formatDate, formatMoney } from 'helpers/helpers';

import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';

function Production() {
    const { t } = useTranslation()
    const statuses = [
        {
            id: 'planned',
            name: t('planned')
        },
        {
            id: 'in_progress',
            name: t('in_work') // В JSON "В работе" — это 'in_work'
        },
        {
            id: 'completed',
            name: t('completed')
        },
        {
            id: 'cancelled',
            name: t('cancelled')
        },
    ];

    const [dropdown, setDropdown] = useState(false)
    const [recipeModal, setRecipeModal] = useState({ show: false, id: '' })


    const initFilterData = {
        page: 1,
        page_size: 20,
        search: '',
        status: '',
    }

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({})
    const [products, setProducts] = useState([])

    async function exportFile() {
        await DOWNLOAD_FILE('/production/orders/export/', filterData, t('production'))
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/production/dashboard/', filterData),
            GET('/production/orders/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, results: responseStats.data, });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);

    const getProducts = useCallback(async () => {
        const response = await GET('/catalogs/products/');

        if (httpOk(response)) {
            setProducts(response.data);
        }
    }, []);

    useEffect(() => {
        getProducts();
    }, [getProducts]);


    return (
        <>
            <div className="d-between">
                <div>
                    <div className="main-content-title">
                        {t('production')}
                    </div>

                    <div className="main-content-subtitle">
                        {t('production_orders_management')}
                    </div>
                </div>
            </div>

            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('total_orders')}
                            </div>
                            <div className="value">
                                {formatMoney(data?.counters?.total)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_work')}
                            </div>
                            <div className="value text-purple">
                                {formatMoney(data?.counters?.in_progress)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('planned')}
                            </div>
                            <div className="value text-primary">
                                {formatMoney(data?.counters?.planned)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('completed')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.counters?.completed_today)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="d-flex gap-16 w-100">
                    <FilterSearch
                        value={filterData.search}
                        onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                        placeholder={t('search_by_sku_or_name')}
                    />

                    <CustomSelect
                        options={statuses}
                        value={statuses.find(option => option.id === filterData?.status) || ''}
                        onChange={(option) => setFilterData({ ...filterData, status: option?.id })}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        placeholder={t('select_status')}
                    />

                    <FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
                        <div className="form-group">
                            <label htmlFor="name">{t('products')}</label>
                            <CustomSelect
                                options={products}
                                value={products.find(option => option.id === filterData?.product_id) || ''}
                                onChange={(option) => setFilterData({ ...filterData, product_id: option?.id })}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                            />
                        </div>
                    </FilterDropdown>
                    <button className="white third" onClick={() => exportFile()}>
                        <DownloadIcon />
                        {t('export')}
                    </button>
                </div>
            </div>

            <div className="card">
                <div className="d-between mb-30">
                    <div className="fz16 text-third">
                        {t('production_orders')}
                    </div>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th style={{ width: '10%' }}>{t('order_id')}</th>
                            <th style={{ width: '10%' }}>{t('sku')}</th>
                            <th style={{ width: '20%' }}>{t('name')}</th>
                            <th style={{ width: '5%' }}>{t('plan')}</th>
                            <th style={{ width: '5%' }}>{t('fact')}</th>
                            <th style={{ width: '10%' }}>{t('progress')}</th>
                            <th style={{ minWidth: '110px' }}>{t('start_date')}</th>
                            <th style={{ minWidth: '110px' }}>{t('end_date')}</th>
                            <th style={{ width: '10%' }}>{t('status')}</th>
                            <th style={{ width: '10%' }}>{t('actions')}</th>
                        </tr>
                    </thead>

                    <tbody>
                        {data?.results?.map((item, index) => (
                            <tr key={index}>
                                <td>{item.order_number}</td>
                                <td>{item.order_number}</td>
                                <td>{item.product_name}</td>
                                <td>{formatMoney(item.planned_quantity)} {item.unit_short_name}</td>
                                <td>{formatMoney(item.produced_quantity)} {item.unit_short_name}</td>
                                <td>
                                    <div className="d-flex align-items-center gap-8">
                                        <div className="w-100">
                                            <div className="progress-bar">
                                                <div className="progress-percent" style={{ width: `${item.progress_percent}%` }}></div>
                                            </div>
                                        </div>
                                        <div>
                                            {formatMoney(item.progress_percent)}%
                                        </div>
                                    </div>
                                </td>
                                <td>{formatDate(item.planned_start_date)}</td>
                                <td>{formatDate(item.planned_end_date)}</td>
                                <td>
                                    <div className={`alert alert-${item.status} w-fit-content`}>
                                        {t(item.status_label)}
                                    </div>
                                </td>
                                <td>
                                    <div className="d-flex align-items-center gap-8">
                                        <button className="transparent small" onClick={() => setRecipeModal({ show: true, id: item.id })}>
                                            {t('details')}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>


                <Pagination
                    paginate={handlePaginate}
                    pagination={{
                        total: data?.count || 0
                    }}
                />
            </div>

            <RecipeModal modal={recipeModal.show} setModal={() => setRecipeModal({ show: false })} id={recipeModal.id} />
        </>
    )
}

export default Production